/**
 * TUVI - Componente PDF de Cotizacion
 * Renderiza la cotizacion en formato HTML para generar PDF multi-pagina
 * Patron basado en Contratos.jsx (jsPDF + html2canvas)
 */

import React, { forwardRef } from 'react';
import { BACKEND_URL } from '../../config';

// Formatear moneda colombiana
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Opciones de bano (mismas del componente principal)
const OPCIONES_BANO = [
  { id: 'conserva_todos', label: 'Se conservan todos los elementos entregados por la constructora en el area del bano' },
  { id: 'conserva_enchape', label: 'Se conserva enchape de piso y paredes' },
  { id: 'conserva_sanitario', label: 'Se conserva sanitario' },
  { id: 'cambia_puerta', label: 'Se cambia puerta de bano' },
  { id: 'conserva_lavamanos_griferia', label: 'Se conserva lavamanos y griferia' },
  { id: 'cambia_enchape_completo', label: 'Se cambia enchape completo' },
  { id: 'conserva_regadera', label: 'Se conserva regadera y mezclador' },
  { id: 'cambia_lavamanos', label: 'Se cambia solo lavamanos' },
  { id: 'conserva_accesorios', label: 'Se conserva Kit de accesorios ceramicos' },
  { id: 'renueva_completo', label: 'Se renueva bano completamente (Aparatos sanitarios, griferias y enchapes)' }
];

// Formas de cocina
const FORMAS_COCINA = [
  { id: 'Lineal', label: 'Lineal', imagen: '/Formas de cocina/Lineal.png' },
  { id: 'Lineal+Isla', label: 'Lineal + Isla', imagen: '/Formas de cocina/Lineal+Isla.png' },
  { id: 'EnL', label: 'En L', imagen: '/Formas de cocina/EnL.png' },
  { id: 'EnC', label: 'En C', imagen: '/Formas de cocina/EnC.png' }
];

// Observaciones fijas
const OBSERVACIONES = [
  'Los precios de la presente propuesta tienen una validez de 30 dias calendario a partir de la fecha de expedicion.',
  'Los valores presentados incluyen Administracion, Imprevistos y Utilidad (AIU) e IVA.',
  'El plazo de entrega comenzara a correr una vez se firme el contrato de obra y se realice el primer desembolso acordado.',
  'Los acabados seran de primera calidad, utilizando marcas reconocidas del mercado nacional.',
  'Las actividades no contempladas en la presente propuesta seran cotizadas como adicionales previo acuerdo con el propietario.',
  'TUVI garantiza todos los trabajos realizados por un periodo de un (1) ano contado a partir de la fecha de entrega del inmueble.'
];

// Consideraciones especiales fijas
const CONSIDERACIONES = [
  'Cualquier modificacion estructural que se requiera debera contar con la aprobacion previa de la constructora del edificio.',
  'Los horarios de trabajo seran acordados con la administracion del conjunto o edificio, respetando los reglamentos de propiedad horizontal.',
  'El material existente que sea retirado durante la remodelacion sera de propiedad del propietario, salvo indicacion contraria por escrito.'
];

// Qué cantidades se imprimen: las unidades (UND) siempre, y los metros lineales
// (ML) solo en carpintería (CARPINTERIA EN MADERA y CARPINTERIA ESPECIAL) y en
// MESONES. En las demás categorías los ML confundían al cliente.
const sinTildes = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();
const CATEGORIAS_CON_ML = ['CARPINTERIA', 'MESON'];

const mostrarCantidad = (act) => {
  const unidad = sinTildes(act.unidad);
  if (unidad === 'UND') return true;
  if (unidad !== 'ML') return false;
  const categoria = sinTildes(act.categoria || act.capitulo);
  return CATEGORIAS_CON_ML.some(c => categoria.includes(c));
};

// Ya no se pagina - todo en una sola hoja continua

const CotizacionPDF = forwardRef(({ cotizacion, todosLosEspacios = [] }, ref) => {
  if (!cotizacion) return null;

  const {
    consecutivo, fecha, nombre_cliente, cedula_cliente, telefono_cliente, correo_cliente,
    proyecto_nombre, apartamento, area_m2, direccion, habitaciones, banos,
    forma_cocina, opciones_bano, plano_url, plano_nombre,
    subtotal, aiu_porcentaje, aiu_valor, iva_valor, valor_final, descuento, valor_final_descuento,
    actividades, plan_pagos, tipo
  } = cotizacion;

  // Agrupar actividades por espacio (deduplicando por id). En las cotizaciones
  // especiales no se deduplica: ahí una misma actividad puede ir varias veces.
  const esEspecial = tipo === 'especial';
  const actividadesPorEspacio = {};
  const idsVistos = new Set();
  (actividades || []).forEach(act => {
    // Deduplicar por id de actividad
    const actKey = act.actividad_id ? `${act.actividad_id}-${act.espacio || ''}` : `${act.id}-${act.descripcion}`;
    if (!esEspecial && idsVistos.has(actKey)) return;
    idsVistos.add(actKey);

    const esp = act.espacio || 'GENERALES';
    if (!actividadesPorEspacio[esp]) {
      actividadesPorEspacio[esp] = [];
    }
    actividadesPorEspacio[esp].push(act);
  });

  // Orden definido de espacios
  const ORDEN_ESPACIOS_PDF = ['Generales', 'Cocina', 'Habitaciones', 'Estudio', 'Baños', 'Zona de Ropas', 'Balcón / Terraza', 'Finales'];
  const todosEspaciosRaw = todosLosEspacios.length > 0 ? todosLosEspacios : Object.keys(actividadesPorEspacio);
  // Deduplicar espacios
  const todosEspacios = [...new Set(todosEspaciosRaw)];
  const espaciosConActividades = todosEspacios.filter(esp => (actividadesPorEspacio[esp] || []).length > 0);
  // Ordenar por el orden definido
  const espaciosOrdenados = espaciosConActividades.sort((a, b) => {
    const idxA = ORDEN_ESPACIOS_PDF.findIndex(e => e.toLowerCase() === a.toLowerCase());
    const idxB = ORDEN_ESPACIOS_PDF.findIndex(e => e.toLowerCase() === b.toLowerCase());
    return (idxA >= 0 ? idxA : ORDEN_ESPACIOS_PDF.length) - (idxB >= 0 ? idxB : ORDEN_ESPACIOS_PDF.length);
  });

  // Numeracion global de items
  let itemGlobal = 0;

  // URL del plano: MinIO si empieza con "minio:", sino proxy legacy
  const planoProxyUrl = (() => {
    if (!plano_url) return null;
    if (plano_url.startsWith('minio:')) {
      const key = plano_url.replace('minio:planos/', '');
      return `https://api.tuvisistema.com/upload/plano-img/${encodeURIComponent(key)}`;
    }
    return `${BACKEND_URL}/api/documentos/proxy-imagen.php?url=${encodeURIComponent(plano_url)}`;
  })();

  // Valor final a mostrar
  const valorFinalMostrar = (parseFloat(descuento) > 0) ? valor_final_descuento : valor_final;

  // Último día del mes actual en formato dd-mm-aaaa
  const hoy = new Date();
  const ultimoDiaMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  const validezOferta = `${String(ultimoDiaMes.getDate()).padStart(2, '0')}-${String(ultimoDiaMes.getMonth() + 1).padStart(2, '0')}-${ultimoDiaMes.getFullYear()}`;

  // Posible fecha de entrega: el campo `fecha` de la cotización (así lo captura
  // el formulario). Se muestra en formato dd-mm-aaaa, igual que la validez.
  const fechaEntrega = (() => {
    if (!fecha) return '';
    const soloFecha = String(fecha).slice(0, 10); // YYYY-MM-DD
    const [y, m, d] = soloFecha.split('-');
    return (y && m && d) ? `${d}-${m}-${y}` : soloFecha;
  })();

  // Opciones bano parseadas
  const opcionesBano = typeof opciones_bano === 'string' ? JSON.parse(opciones_bano || '{}') : (opciones_bano || {});

  return (
    <div ref={ref}>
      {/* HOJA UNICA CONTINUA */}
      <div className="cotizacion-page">
        {/* Banner */}
        <div className="pdf-banner">
          <img src="/logo_tuvi_banner.jpg" alt="TUVI" className="pdf-banner-logo" crossOrigin="anonymous" />
          <span className="pdf-banner-title">COTIZACIÓN</span>
        </div>

        {/* Contenido */}
        <div style={{ padding: '65px 65px 0' }}>
          {/* Fecha de Cotizacion - arriba a la derecha */}
          <div className="pdf-fecha-cotizacion">
            <span className="pdf-fecha-lbl">Fecha de Cotizacion</span>
            <span className="pdf-fecha-val">{fecha}</span>
          </div>

          {/* Datos del Propietario e Inmueble - 2 columnas */}
          <div className="pdf-datos-wrapper">
            {/* Columna izquierda: Propietario */}
            <div className="pdf-datos-left">
              <table className="pdf-datos-block">
                <thead>
                  <tr>
                    <th colSpan="2" className="pdf-datos-title-dark">Datos del Propietario</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pdf-dato-lbl">Nombre</td>
                    <td className="pdf-dato-val">{nombre_cliente}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Cedula</td>
                    <td className="pdf-dato-val">{cedula_cliente}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Telefono</td>
                    <td className="pdf-dato-val">{telefono_cliente}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Correo</td>
                    <td className="pdf-dato-val">{correo_cliente}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Columna derecha: Inmueble */}
            <div className="pdf-datos-right">
              <table className="pdf-datos-block">
                <thead>
                  <tr>
                    <th colSpan="4" className="pdf-datos-title-dark">Datos del Inmueble</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pdf-dato-lbl">Proyecto</td>
                    <td colSpan="3" className="pdf-dato-val">{proyecto_nombre}</td>
                  </tr>
                  <tr className="pdf-separator-row"><td colSpan="4"></td></tr>
                  <tr>
                    <td className="pdf-dato-lbl">Area</td>
                    <td className="pdf-dato-val">{area_m2}</td>
                    <td className="pdf-dato-lbl">Habitaciones</td>
                    <td className="pdf-dato-val">{habitaciones}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Baños</td>
                    <td className="pdf-dato-val">{banos}</td>
                    <td className="pdf-dato-lbl">Apartamento</td>
                    <td className="pdf-dato-val">{apartamento}</td>
                  </tr>
                  <tr className="pdf-separator-row"><td colSpan="4"></td></tr>
                  <tr>
                    <td className="pdf-dato-lbl" colSpan="3">Fecha probable de Entrega del apartamento</td>
                    <td className="pdf-dato-val pdf-val-green">{fechaEntrega}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl" colSpan="3">Validez de la oferta</td>
                    <td className="pdf-dato-val pdf-val-green">{validezOferta}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Forma de cocina */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '8px' }}>
            <h4 className="pdf-section-beige">Forma de cocina</h4>
            <div className="pdf-cocina-row">
              {FORMAS_COCINA.map(forma => (
                <div key={forma.id} className="pdf-cocina-item-h">
                  <span className={`pdf-check-box ${forma_cocina === forma.id ? 'checked' : ''}`}>
                    {forma_cocina === forma.id ? '✓' : ''}
                  </span>
                  <img src={forma.imagen} alt={forma.label} crossOrigin="anonymous" />
                </div>
              ))}
            </div>
          </div>

          {/* Bano que entrega la constructora */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '6px' }}>
            <h4 className="pdf-section-beige">Bano que entrega la constructora</h4>
            <div className="pdf-bano-grid">
              {OPCIONES_BANO.map(opcion => (
                <div key={opcion.id} className="pdf-bano-item">
                  <span className={`pdf-check-box ${opcionesBano[opcion.id] ? 'checked' : ''}`}>
                    {opcionesBano[opcion.id] ? '✓' : ''}
                  </span>
                  <span className="pdf-bano-text">{opcion.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Propuesta economica - Plano */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '6px' }}>
            <h4 className="pdf-section-beige">Propuesta economica</h4>
            {planoProxyUrl && (
              <img
                src={planoProxyUrl}
                alt={plano_nombre}
                className="pdf-plano-img"
                crossOrigin="anonymous"
              />
            )}
          </div>

          {/* ACTIVIDADES DE REMODELACION */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '10px', marginBottom: '5px' }}>
            <h4 className="pdf-section-beige">ACTIVIDADES DE REMODELACION</h4>
          </div>
          <table className="pdf-actividades-table">
            <thead>
              <tr>
                <th style={{ width: '30px' }}>Item</th>
                <th>Descripcion</th>
                <th style={{ width: '35px' }}>Und</th>
                <th style={{ width: '35px' }}>Cant</th>
                <th style={{ width: '80px' }} className="text-right">V. Total</th>
              </tr>
            </thead>
            <tbody>
              {espaciosOrdenados.map((espacio, gIdx) => {
                const acts = actividadesPorEspacio[espacio] || [];
                const rows = [];
                const totalEspacio = acts.reduce((sum, act) => sum + (parseFloat(act.valor_total) || 0), 0);
                // 2 filas en blanco entre espacios
                if (gIdx > 0) {
                  rows.push(
                    <tr key={`sep1-${gIdx}`} className="pdf-espacio-separator"><td colSpan="5"></td></tr>,
                    <tr key={`sep2-${gIdx}`} className="pdf-espacio-separator"><td colSpan="5"></td></tr>
                  );
                }
                rows.push(
                  <tr key={`esp-${gIdx}`} className="espacio-header">
                    <td colSpan="4">{espacio}</td>
                    <td className="text-right">{formatCurrency(totalEspacio)}</td>
                  </tr>
                );
                acts.forEach((act, aIdx) => {
                  itemGlobal++;
                  rows.push(
                    <tr key={`act-${gIdx}-${aIdx}`} className={aIdx % 2 === 1 ? 'pdf-act-row-beige' : ''}>
                      <td>{itemGlobal}</td>
                      <td>{act.descripcion}</td>
                      <td>{act.unidad}</td>
                      <td>{mostrarCantidad(act) ? act.cantidad : ''}</td>
                      <td className="text-right">{formatCurrency(act.valor_total)}</td>
                    </tr>
                  );
                });
                return rows;
              })}
              <tr className="subtotal-row">
                <td colSpan="4" className="text-right">SUBTOTAL</td>
                <td className="text-right">{formatCurrency(subtotal)}</td>
              </tr>
            </tbody>
          </table>

          {/* Resumen financiero */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '15px' }}>
            <h4 className="pdf-section-beige">RESUMEN FINANCIERO</h4>
            <table className="pdf-financial-table">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td className="text-right">{formatCurrency(subtotal)}</td>
                </tr>
                <tr>
                  <td>AIU ({aiu_porcentaje}%)</td>
                  <td className="text-right">{formatCurrency(aiu_valor)}</td>
                </tr>
                <tr>
                  <td>IVA</td>
                  <td className="text-right">{formatCurrency(iva_valor)}</td>
                </tr>
                {parseFloat(descuento) > 0 && (
                  <tr className="descuento-row">
                    <td>Descuento</td>
                    <td className="text-right">-{formatCurrency(descuento)}</td>
                  </tr>
                )}
                <tr className="valor-final-row">
                  <td>VALOR FINAL DEL PROYECTO</td>
                  <td className="text-right">{formatCurrency(valorFinalMostrar)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Plan de pagos */}
          {plan_pagos && plan_pagos.length > 0 && (
            <div className="pdf-plan-pagos-wrapper">
              <table className="pdf-plan-pagos-table">
                <thead>
                  <tr>
                    <th colSpan="2">Plan de pagos</th>
                  </tr>
                </thead>
                <tbody>
                  {plan_pagos.map((pago, idx) => (
                    <tr key={idx} className={idx % 2 === 1 ? 'pdf-plan-row-beige' : ''}>
                      <td className="text-right">{pago.descripcion} - {pago.porcentaje}%</td>
                      <td className="text-right">{formatCurrency(pago.valor || (valorFinalMostrar * pago.porcentaje / 100))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Observaciones */}
          <div className="cotizacion-pdf-section pdf-section-small" style={{ marginTop: '10px' }}>
            <h4 className="pdf-section-beige">OBSERVACIONES</h4>
            <div className="pdf-observaciones">
              {OBSERVACIONES.map((obs, idx) => (
                <p key={idx}>&#8226; {obs}</p>
              ))}
            </div>
          </div>

          {/* Consideraciones especiales */}
          <div className="cotizacion-pdf-section pdf-section-small" style={{ marginTop: '10px' }}>
            <h4 className="pdf-section-beige">CONSIDERACIONES ESPECIALES</h4>
            <div className="pdf-consideraciones">
              {CONSIDERACIONES.map((con, idx) => (
                <p key={idx}>&#8226; {con}</p>
              ))}
            </div>
          </div>

          {/* Aceptación del cliente: firma a satisfacción, antes de la firma de TUVI (imagen final).
              Va dentro de .cotizacion-pdf-section para que al imprimir no se corte en mitad. */}
          <div className="cotizacion-pdf-section pdf-aceptacion" style={{ marginTop: '12px' }}>
            <h4 className="pdf-section-beige">ACEPTACIÓN DEL CLIENTE</h4>
            <p className="pdf-aceptacion-texto">
              Declaro que recibí y revisé esta cotización y acepto a satisfacción su alcance, actividades, valores, plan de pagos, observaciones y consideraciones especiales.
            </p>
            <div className="pdf-aceptacion-grid">
              <div className="pdf-aceptacion-firma">
                <div className="pdf-aceptacion-linea"></div>
                <span className="pdf-aceptacion-lbl">Firma del cliente</span>
              </div>
              <div className="pdf-aceptacion-datos">
                <div className="pdf-aceptacion-campo"><span className="pdf-aceptacion-lbl">Nombre</span><span className="pdf-aceptacion-val">{nombre_cliente}</span></div>
                <div className="pdf-aceptacion-campo"><span className="pdf-aceptacion-lbl">Cédula</span><span className="pdf-aceptacion-val">{cedula_cliente}</span></div>
                <div className="pdf-aceptacion-campo"><span className="pdf-aceptacion-lbl">Fecha de aceptación</span><span className="pdf-aceptacion-val"></span></div>
              </div>
            </div>
          </div>

        </div>

        {/* Imagen final - fuera del padding para ancho completo */}
        <div className="pdf-imagen-final">
          <img src="/final_cotizador.png" alt="TUVI" crossOrigin="anonymous" />
        </div>
      </div>
    </div>
  );
});

CotizacionPDF.displayName = 'CotizacionPDF';

export default CotizacionPDF;
